/**
 * RANS — V100 (local) solve client (dev-only, FXC-9009).
 *
 * A third high-lift solve backend besides "RANS (V100/cloud)" and
 * "WebGPU (in-browser)": a FREE / FAST / LOCAL high-fidelity 2D RANS path that
 * runs on the V100 box instead of forking a cloud Flow360 case. Same
 * browser → box HTTP service → solve → result architecture as the Phase C
 * 2nd-order Euler refine (`webgpu-solver/refineClient.ts`):
 *
 *   POST /rans   {contours, alpha, mach, reynolds}  → 202 {job_id}
 *   GET  /rans/{job_id}                              → {status, result?}
 *
 * The service (`tools/rans_v100_service.py` in the FlexFoil_RANS repo) shells out
 * to the proven Flow360 RANS pipeline runner on the box:
 *   run_case.py <contours> -o <out> --solve --compute-root <clean> --gpu <0..3>
 * and parses total_forces_v2.csv + surface_forces_v2.csv on completion. A
 * converged 2-element solve is ~1–6 min, so this is a JOB pattern (POST returns a
 * job id; we poll GET to completion).
 *
 * Gating: the workbench only surfaces this backend when `VITE_RANS_V100_URL` is
 * set (see `HighliftMultifidelitySurface.tsx`), so the default build is
 * untouched — it appears only in dev when you point it at the tunnelled service
 * (default `http://127.0.0.1:8078`, reached over `ssh -L 8078:127.0.0.1:8078`).
 *
 * The contours payload is the SAME run_case.py config object the box pipeline
 * expects (elements[].contour + farfield + flow + mesh + solver), built from the
 * live workbench geometry by `buildRansV100Contours`.
 */

import type { SweepDisk, SweepInputs, V2 } from "./geometry";
import type { FlowMesh } from "./foil/FlowVisLayer";
import { getAuthToken } from "../../api/client";

/** Authorization header for the V100 service. On preprod the service is reached via a
 *  tailscale-funnel that requires a valid @flexcompute SSO token; we forward the user's
 *  Thread access token. Local dev hits the box via the SSH tunnel (no session, header
 *  omitted) and the service leaves that path open. Best-effort: never block a solve on
 *  token retrieval. (FXC-9009) */
async function v100AuthHeaders(): Promise<Record<string, string>> {
  try {
    const token = await getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}
// (FlowMesh used by RansV100Result.flow + fetchRansV100Flow below)

/** Default dev endpoint — overridden by `VITE_RANS_V100_URL`. */
export const DEFAULT_RANS_V100_URL = "http://127.0.0.1:8078";

/** Read the configured V100 endpoint (or the loopback default). */
export function ransV100BaseUrl(): string {
  const url = import.meta.env.VITE_RANS_V100_URL;
  return (typeof url === "string" && url.trim()) || DEFAULT_RANS_V100_URL;
}

/** True when the V100 backend is configured — gate the toggle option on this. */
export function ransV100Enabled(): boolean {
  return typeof import.meta.env.VITE_RANS_V100_URL === "string"
    && import.meta.env.VITE_RANS_V100_URL.trim().length > 0;
}

/** The coefficients the V100 solve returns, normalised to the workbench's camelCase. */
export interface RansV100Result {
  /** Total section lift coefficient. */
  cl: number;
  /** Total section (pressure) drag coefficient. */
  cd: number;
  /** L/D, when CD is finite. */
  lOverD?: number;
  /** Converged step the forces were read at. */
  step?: number;
  /** Per-element wind-axis CL (e.g. clMain, clFlap, clVane) — present elements only. Legacy aliases
   *  for the first-three elements; the full N-element breakdown is in {@link elements}. */
  clMain?: number;
  clVane?: number;
  clFlap?: number;
  /** Per-element wind-axis CD (legacy first-three aliases). */
  cdMain?: number;
  cdVane?: number;
  cdFlap?: number;
  /** Full per-element coefficient breakdown (FXC-9009 N-element wire), in solve order. Names match
   *  the wire `elements[].name` (main/vane/flap for the default triple, the element id for extras).
   *  `clMain`/`clVane`/`clFlap` above remain as back-compat aliases for the first three. */
  elements?: { name: string; cl?: number; cd?: number }[];
  alphaDeg?: number;
  mach?: number;
  reynolds?: number;
  /** The solved flowfield slice (FXC-9009: fetched from /rans/{id}/flow). null if unavailable. */
  flow?: FlowMesh | null;
}

/** Flow + mesh conditions for the solve (alpha is per-point; the rest default sensibly). */
export interface RansV100FlowInputs {
  alphaDeg: number;
  mach: number;
  reynolds: number;
  /** Static temperature (K); default 288.15. */
  temperature?: number;
}

/** An actuator-disk blowing source on the run_case.py contours payload (snake_case for the box). */
export interface RansV100Disk {
  center: [number, number];
  radius: number;
  thickness: number;
  axis: [number, number];
  loading: number;
}

/** A self-contained run_case.py contours config (the box pipeline's input). */
export interface RansV100Contours {
  elements: { name: string; contour: V2[]; is_wall: boolean }[];
  /** Actuator-disk blowing sources (FXC-9009 N-disk wire). Empty when blowing is off. The box
   *  pipeline consumes these as momentum sources; previously the V100 path dropped blowing entirely. */
  disks: RansV100Disk[];
  /** Back-compat alias for the first blowing source; legacy box runners still read `disk`. */
  disk?: RansV100Disk;
  farfield: { type: "circle"; center: [number, number]; radius: number; n: number };
  flow: { reynolds: number; mach: number; alpha_deg: number; temperature: number };
  mesh: Record<string, number | boolean>;
  solver: { max_steps: number };
}

/** Tuning the contours payload exposes (sensible defaults match the matched_contours.json case). */
export interface RansV100ContourOptions {
  /** Farfield circle radius in chords (default 50). */
  farfieldRadius?: number;
  /** Farfield boundary segment count (default 240). */
  farfieldN?: number;
  /** RANS y+ target for the first cell (default 1.0). */
  yplus?: number;
  /** Solver step cap requested (the service also clamps to its own hard cap). */
  maxSteps?: number;
}

const DEFAULT_TEMPERATURE_K = 288.15;

/**
 * Serialize the live workbench geometry + flow into the run_case.py contours JSON.
 *
 * `buildSweepInputs(config)` already yields the deployed element contours
 * (`elements[].contour` as `[x,z][]` in chord coordinates) and the flow
 * conditions; we add the α (a per-point knob, not part of the geometry), wrap
 * the section in a farfield circle centred on its area, and attach the RANS mesh
 * + solver blocks the box mesher expects. Empty/degenerate contours are dropped
 * so a not-yet-built element never reaches the mesher.
 */
export function buildRansV100Contours(
  sweep: SweepInputs,
  flow: RansV100FlowInputs,
  opts: RansV100ContourOptions = {},
): RansV100Contours {
  const elements = sweep.elements
    .filter((el) => Array.isArray(el.contour) && el.contour.length >= 3)
    .map((el) => ({
      name: el.name,
      contour: el.contour.map(([x, z]) => [x, z] as V2),
      is_wall: true,
    }));
  if (elements.length === 0) {
    throw new Error("buildRansV100Contours: no element contours to mesh");
  }

  // Farfield centre = the bounding-box centre of all element vertices, so the
  // circle frames the (possibly far-deployed) section rather than the origin.
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const el of elements) {
    for (const [x, z] of el.contour) {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (z < minZ) minZ = z;
      if (z > maxZ) maxZ = z;
    }
  }
  const center: [number, number] = [(minX + maxX) / 2, (minZ + maxZ) / 2];

  // Carry ALL blowing sources through to the box pipeline (FXC-9009 N-disk wire). The V100 path
  // previously dropped blowing entirely (no disk field on the payload). `sweep.disks` is empty when
  // disk loading is 0, so a blow-off design serializes no disks (unchanged behaviour).
  const disks: RansV100Disk[] = (sweep.disks ?? []).map((d: SweepDisk) => ({
    center: [d.center[0], d.center[1]],
    radius: d.radius,
    thickness: d.thickness,
    axis: [d.axis[0], d.axis[1]],
    loading: d.loading,
  }));

  return {
    elements,
    disks,
    ...(disks.length > 0 ? { disk: disks[0]! } : {}),
    farfield: {
      type: "circle",
      center,
      radius: opts.farfieldRadius ?? 50,
      n: opts.farfieldN ?? 240,
    },
    flow: {
      reynolds: flow.reynolds,
      mach: flow.mach,
      alpha_deg: flow.alphaDeg,
      temperature: flow.temperature ?? sweep.flow.temperature ?? DEFAULT_TEMPERATURE_K,
    },
    // RANS y+~1 anisotropic mesh blocks (mirror the matched_contours.json case).
    mesh: {
      span: 0.1,
      nspan: 1,
      yplus: opts.yplus ?? 1.0,
      growth: 1.2,
      hwall: 0.004,
      hmax: 3.0,
      h0: 0.0,
      gradation: true,
    },
    solver: { max_steps: opts.maxSteps ?? 5000 },
  };
}

/** The service's snake_case result dict (mirrors rans_v100_service._extract_*). */
interface RawRansV100Result {
  cl: number;
  cd: number;
  l_over_d?: number | null;
  step?: number | null;
  cl_main?: number;
  cl_vane?: number;
  cl_flap?: number;
  cd_main?: number;
  cd_vane?: number;
  cd_flap?: number;
  /** Full per-element breakdown (FXC-9009 N-element wire), keyed by wire element name. */
  elements?: { name: string; cl?: number; cd?: number }[];
  alpha_deg?: number;
  mach?: number;
  reynolds?: number;
}

/** GET /rans/{job_id} response. */
interface PollResponse {
  status: "queued" | "running" | "done" | "error";
  /** 1-based queue position + jobs-ahead + worker count — present when status === "queued". */
  position?: number;
  ahead?: number;
  workers?: number;
  result?: RawRansV100Result;
  error?: string;
  cl_history?: number[];
}

/** Normalise the service's snake_case dict into the workbench's camelCase result. */
function liftResult(raw: RawRansV100Result): RansV100Result {
  return {
    cl: raw.cl,
    cd: raw.cd,
    lOverD: raw.l_over_d ?? undefined,
    step: raw.step ?? undefined,
    clMain: raw.cl_main,
    clVane: raw.cl_vane,
    clFlap: raw.cl_flap,
    cdMain: raw.cd_main,
    cdVane: raw.cd_vane,
    cdFlap: raw.cd_flap,
    elements: Array.isArray(raw.elements)
      ? raw.elements.map((e) => ({ name: e.name, cl: e.cl, cd: e.cd }))
      : undefined,
    alphaDeg: raw.alpha_deg,
    mach: raw.mach,
    reynolds: raw.reynolds,
  };
}

/** Progress callback surface for the (minutes-long) poll — mirrors refineClient. */
export interface RansV100Progress {
  status: "submitting" | "queued" | "running" | "done" | "error";
  /** Wall-clock seconds since submit (the only honest progress for an opaque solve). */
  elapsedSec: number;
  /** 1-based queue position while status === "queued". */
  position?: number;
  /** Jobs still queued ahead of this one while status === "queued". */
  ahead?: number;
  /** The box job id — lets the caller poll /rans/{id}/flow?step=N for live frames while it solves. */
  jobId?: string;
  /** Downsampled CL-vs-step trajectory from the box (total_forces_v2.csv) — drives the convergence bar. */
  clHistory?: number[];
}

export interface RansV100Options {
  /** Override the base URL (defaults to `ransV100BaseUrl()`). */
  baseUrl?: string;
  /** Poll cadence in ms (default 3000 — the solve is ~1–6 min). */
  pollIntervalMs?: number;
  /** Abort the poll (e.g. on unmount / backend switch). */
  signal?: AbortSignal;
  /** Progress ticks (submit → each poll → done). */
  onProgress?: (p: RansV100Progress) => void;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const id = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(id);
        reject(new DOMException("rans-v100 aborted", "AbortError"));
      },
      { once: true },
    );
  });
}

/**
 * Submit a V100 RANS solve and poll to completion. POSTs `/rans` for a job id,
 * then GETs `/rans/{id}` every `pollIntervalMs` until the status is `done`
 * (resolves the lifted result) or `error` (throws). Honours `signal` so the
 * caller can cancel a long poll on unmount or a backend switch.
 */
export async function solveRansV100(
  contours: RansV100Contours,
  flow: RansV100FlowInputs,
  opts: RansV100Options = {},
): Promise<RansV100Result> {
  const base = (opts.baseUrl ?? ransV100BaseUrl()).replace(/\/$/, "");
  const pollIntervalMs = opts.pollIntervalMs ?? 3000;
  const startedAt = Date.now();
  const elapsed = () => (Date.now() - startedAt) / 1000;

  opts.onProgress?.({ status: "submitting", elapsedSec: 0 });
  const postRes = await fetch(`${base}/rans`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await v100AuthHeaders()) },
    body: JSON.stringify({
      contours,
      alpha: flow.alphaDeg,
      mach: flow.mach,
      reynolds: flow.reynolds,
    }),
    signal: opts.signal,
  });
  if (!postRes.ok) {
    throw new Error(`rans-v100 POST failed: ${postRes.status} ${postRes.statusText}`);
  }
  const { job_id: jobId } = (await postRes.json()) as { job_id?: string };
  if (!jobId) throw new Error("rans-v100 POST returned no job_id");

  // Poll until done. The solve is ~1–6 min; the loop is bounded only by `signal`.
  for (;;) {
    await sleep(pollIntervalMs, opts.signal);
    const pollRes = await fetch(`${base}/rans/${jobId}`, { signal: opts.signal, headers: await v100AuthHeaders() });
    if (!pollRes.ok) {
      throw new Error(`rans-v100 poll failed: ${pollRes.status} ${pollRes.statusText}`);
    }
    const snap = (await pollRes.json()) as PollResponse;
    if (snap.status === "queued") {
      opts.onProgress?.({
        status: "queued", elapsedSec: elapsed(),
        position: snap.position, ahead: snap.ahead, jobId,
      });
      continue;
    }
    if (snap.status === "running") {
      opts.onProgress?.({ status: "running", elapsedSec: elapsed(), jobId, clHistory: snap.cl_history });
      continue;
    }
    if (snap.status === "error") {
      opts.onProgress?.({ status: "error", elapsedSec: elapsed() });
      throw new Error(snap.error ?? "rans-v100 job errored");
    }
    // done
    opts.onProgress?.({ status: "done", elapsedSec: elapsed(), jobId, clHistory: snap.cl_history });
    if (!snap.result) throw new Error("rans-v100 job done with no result");
    const result = liftResult(snap.result);
    // FXC-9009: also pull the solved FIELD so the workbench can render it (best-effort — a
    // missing slice must not fail the solve, which still carries valid CL/CD).
    result.flow = await fetchRansV100Flow(jobId, { baseUrl: base, signal: opts.signal }).catch(() => null);
    return result;
  }
}

/** Fetch a solved (or in-progress) V100 slice as a FlowMesh. `step` selects an intermediate
 *  pseudo-step frame (when frame-dumping is on); omit for the final converged field. */
export async function fetchRansV100Flow(
  jobId: string,
  opts: { baseUrl?: string; step?: number; signal?: AbortSignal } = {},
): Promise<FlowMesh | null> {
  const base = (opts.baseUrl ?? ransV100BaseUrl()).replace(/\/$/, "");
  const q = opts.step != null ? `?step=${opts.step}` : "";
  const res = await fetch(`${base}/rans/${jobId}/flow${q}`, { signal: opts.signal, headers: await v100AuthHeaders() });
  if (!res.ok) return null;
  const fm = (await res.json()) as FlowMesh;
  return fm && Array.isArray(fm.points) && fm.points.length > 0 ? fm : null;
}

/** List the pseudo-step frames available for a job (for live convergence playback). */
export async function fetchRansV100FrameSteps(
  jobId: string,
  opts: { baseUrl?: string; signal?: AbortSignal } = {},
): Promise<number[]> {
  const base = (opts.baseUrl ?? ransV100BaseUrl()).replace(/\/$/, "");
  const res = await fetch(`${base}/rans/${jobId}/flow?steps=1`, { signal: opts.signal, headers: await v100AuthHeaders() });
  if (!res.ok) return [];
  const j = (await res.json()) as { steps?: number[] };
  return Array.isArray(j.steps) ? j.steps : [];
}
